import React from "react";
import styled from "styled-components";
import {
  FormRenderer,
  PageHeading,
  SectionCard,
  formTheme,
} from "@120wateraudit/form-builder-components";
import type { FormConfig } from "@120wateraudit/form-builder-components";

interface PreviewStepProps {
  config?: FormConfig;
}

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const EmptyContainer = styled.div`
  max-width: 640px;
  margin: 0 auto;
  width: 100%;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
`;

const EmptyIcon = styled.div`
  font-size: 36px;
  margin-bottom: 12px;
`;

const EmptyTitle = styled.div`
  font-family: ${formTheme.fontBody};
  font-size: 16px;
  font-weight: 600;
  color: ${formTheme.text};
  margin-bottom: 6px;
`;

const EmptyDescription = styled.div`
  font-family: ${formTheme.fontBody};
  font-size: 14px;
  color: ${formTheme.textSecondary};
  line-height: 1.5;
  max-width: 400px;
  margin: 0 auto;
`;

const PreviewBanner = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 10px 16px;
  background: ${formTheme.accentBg};
  border-bottom: 1px solid ${formTheme.accentBorder};
  font-family: ${formTheme.fontBody};
  font-size: 13px;
  color: ${formTheme.accent};
  font-weight: 600;
  flex-shrink: 0;
`;

const PreviewStep: React.FC<PreviewStepProps> = ({ config }) => {
  const hasContent = Boolean(
    config?.pages?.some((p) => (p.items?.length ?? 0) > 0)
  );

  if (hasContent && config) {
    return (
      <Container>
        <PreviewBanner>
          &#128065; Preview Mode — This is how utilities will see the form when
          filling it out
        </PreviewBanner>
        <FormRenderer config={config} />
      </Container>
    );
  }

  return (
    <Container>
      <EmptyContainer>
        <PageHeading
          title="Preview"
          subtitle="See how your form will look to utilities when they fill it out."
        />
        <SectionCard>
          <EmptyState>
            <EmptyIcon>&#128221;</EmptyIcon>
            <EmptyTitle>No form fields to preview</EmptyTitle>
            <EmptyDescription>
              Go back to the Form Builder step and add some fields, groups, or
              tables. Once you have content, you&apos;ll see a live preview
              here.
            </EmptyDescription>
          </EmptyState>
        </SectionCard>
      </EmptyContainer>
    </Container>
  );
};

export default PreviewStep;
